import { z } from 'zod';
import { isoDateSchema } from './iso-date.ts';

/**
 * The shape the portal asks for orders in, and the shape it gets back.
 *
 * Lives in contracts because both the API route and the SPA's Reporting view need it,
 * and because the money fields have rules that must not be re-derived per caller.
 */

export const ORDER_STATES = ['confirmed', 'pending', 'refunded', 'cancelled'] as const;
export const orderStateSchema = z.enum(ORDER_STATES);
export type OrderState = z.infer<typeof orderStateSchema>;

/** Dates are plain calendar days, resolved against the store's timezone by the caller. */

export const partnerOrderQuerySchema = z
  .object({
    /**
     * The partner's discount codes — the only thing that attributes a sale.
     *
     * UTM parameters are not used here at all: they track traffic in GA4, and the
     * two systems are joined on the partner, not on a per-order tag.
     */
    discountCodes: z.array(z.string().min(1)).min(1).max(50),
    /** Inclusive. Omit both for "all time". */
    from: isoDateSchema.optional(),
    /** Inclusive — the service widens it to the end of that day. */
    to: isoDateSchema.optional(),
    /** Shopify caps a page at 250. */
    limit: z.number().int().min(1).max(250).default(50),
    cursor: z.string().min(1).optional(),
    /** Test orders are excluded unless this is set. */
    includeTest: z.boolean().default(false),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'from must not be after to',
    path: ['from'],
  });

export type PartnerOrderQuery = z.infer<typeof partnerOrderQuerySchema>;

export const moneySchema = z.object({
  amount: z.string(),
  currency: z.string().length(3),
});
export type Money = z.infer<typeof moneySchema>;

export const partnerOrderSchema = z.object({
  id: z.string(),
  /** The human order number, e.g. "#SPK-91042". */
  name: z.string(),
  createdAt: z.string(),
  processedAt: z.string(),
  state: orderStateSchema,
  /** Every code on the order, not just the partner's. */
  discountCodes: z.array(z.string()),
  /** The subset that matched the requested partner codes. */
  matchedCodes: z.array(z.string()),
  /**
   * What the order is worth now, after edits and refunds — Shopify's
   * currentTotalPriceSet, in the SHOP's currency. Never sum presentment amounts
   * across orders: in a multi-currency store they are different units.
   */
  total: moneySchema,
  /** What it was worth before any refund or edit. Kept for audit, not for summing. */
  originalTotal: moneySchema,
  refunded: moneySchema,
  customerLabel: z.string().nullable(),
  lineItems: z.array(
    z.object({
      title: z.string(),
      quantity: z.number().int(),
      total: moneySchema,
      sku: z.string().nullable(),
    }),
  ),
});

export type PartnerOrder = z.infer<typeof partnerOrderSchema>;

export const partnerOrderPageSchema = z.object({
  orders: z.array(partnerOrderSchema),
  pageInfo: z.object({
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable(),
  }),
  /** What the service actually sent to Shopify. Logged, and useful in support. */
  appliedQuery: z.string(),
});

export type PartnerOrderPage = z.infer<typeof partnerOrderPageSchema>;
